import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SimulationsPage from "@/pages/SimulationsPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import RiskProfilesPage from "@/pages/RiskProfilesPage";
import { toast } from "sonner";
import { Sidebar } from "@/components/trading/Sidebar";
import { TopBar } from "@/components/trading/TopBar";
import { PortfolioSummary } from "@/components/trading/PortfolioSummary";
import { PriceTicker } from "@/components/trading/PriceTicker";
import { PriceChart } from "@/components/trading/PriceChart";
import { SignalFeed } from "@/components/trading/SignalFeed";
import { NewsFeed } from "@/components/trading/NewsFeed";
import { PositionsTable } from "@/components/trading/PositionsTable";
import { TradeHistory } from "@/components/trading/TradeHistory";
import { BotCommentary } from "@/components/trading/BotCommentary";
import { ConnectBinanceDialog } from "@/components/trading/ConnectBinanceDialog";
import { AddCoinDialog } from "@/components/trading/AddCoinDialog";
import { BotManagementPanel } from "@/components/trading/BotManagementPanel";
import { BotPerformancePanel } from "@/components/trading/BotPerformancePanel";
import { StrategyPanel } from "@/components/trading/StrategyPanel";
import { OpportunityCorner } from "@/components/trading/OpportunityCorner";
import { FuturesPositions } from "@/components/trading/FuturesPositions";
import { FuturesWalletCard } from "@/components/trading/FuturesWalletCard";
import { AdminPanel } from "@/components/trading/AdminPanel";
import { ApiSettings } from "@/components/trading/ApiSettings";
import { LiveTrades } from "@/components/trading/LiveTrades";
import { LivePnlCard } from "@/components/trading/LivePnlCard";
import { Profile } from "@/components/trading/Profile";
import { BottomNav } from "@/components/trading/BottomNav";
import { Notifications } from "@/components/trading/Notifications";
import { COPYRIGHT } from "@/lib/legalTexts";
import { useAuth } from "@/contexts/AuthContext";
import {
  getPrices, getPortfolio, getSignals, getNews, getCommentary, getTrades,
  runAnalyze, approveSignal, rejectSignal, resetPortfolio, getGateStatus, getGateFutures,
  getWatchlist, removeWatchlist, getAutoRefresh, setAutoRefresh, authLogout, getBot, getBotPerformance,
  getOpportunities, getFutures, startBot,
} from "@/lib/api";

const researchPaths = { simulations: "/simulations", reconciliation: "/reconciliation", "risk-profiles": "/risk-profiles" };

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [prices, setPrices] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [signals, setSignals] = useState([]);
  const [news, setNews] = useState([]);
  const [commentary, setCommentary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [binance, setBinance] = useState(null);
  const [autoRefresh, setAutoRefreshState] = useState(false);
  const [bot, setBot] = useState(null);
  const botFetchId = useRef(0);  // guards against a stale getBot overwriting fresher bot state
  const [perf, setPerf] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [futures, setFutures] = useState([]);
  const [gateFutures, setGateFutures] = useState(null);

  const [selected, setSelected] = useState("BTC");
  const [active, setActive] = useState(Object.keys(researchPaths).find((key) => researchPaths[key] === location.pathname) || "overview");
  const onNavigate = (section) => { setActive(section); navigate(researchPaths[section] || "/"); };
  useEffect(() => {
    const section = Object.keys(researchPaths).find((key) => researchPaths[key] === location.pathname);
    setActive((previous) => section || (researchPaths[previous] ? "overview" : previous));
  }, [location.pathname]);
  const [collapsed, setCollapsed] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [binanceOpen, setBinanceOpen] = useState(false);
  const [addCoinOpen, setAddCoinOpen] = useState(false);

  const refreshLight = useCallback(async () => {
    try {
      const [p, pf] = await Promise.all([getPrices(), getPortfolio()]);
      setPrices(p); setPortfolio(pf);
    } catch { /* silent */ }
  }, []);

  const refreshAll = useCallback(async () => {
    const bid = ++botFetchId.current;
    const results = await Promise.allSettled([
      getPrices(), getPortfolio(), getSignals(), getNews(), getCommentary(),
      getTrades(), getGateStatus(), getAutoRefresh(), getBot(), getBotPerformance(),
      getOpportunities(), getFutures(), getGateFutures(),
    ]);
    const [p, pf, s, n, c, t, b, ar, bo, pe, op, fu, gf] = results;
    if (p.status === "fulfilled") { setPrices(p.value);
      if (p.value[0] && !p.value.find((x) => x.symbol === selected)) setSelected(p.value[0].symbol); }
    if (pf.status === "fulfilled") setPortfolio(pf.value);
    if (s.status === "fulfilled") setSignals(s.value);
    if (n.status === "fulfilled") setNews(n.value);
    if (c.status === "fulfilled") setCommentary(c.value);
    if (t.status === "fulfilled") setTrades(t.value);
    if (b.status === "fulfilled") setBinance(b.value);
    if (ar.status === "fulfilled") setAutoRefreshState(ar.value.enabled);
    if (bo.status === "fulfilled" && bid === botFetchId.current) setBot(bo.value);
    if (pe.status === "fulfilled") setPerf(pe.value);
    if (op.status === "fulfilled") setOpportunities(op.value);
    if (fu.status === "fulfilled") setFutures(fu.value);
    if (gf.status === "fulfilled") setGateFutures(gf.value);
  }, [selected]);

  useEffect(() => {
    refreshAll();
    const iv = setInterval(refreshLight, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll bot/signals/commentary while bot or auto-refresh is active
  useEffect(() => {
    if (!bot?.active && !autoRefresh) return;
    const iv = setInterval(async () => {
      const bid = ++botFetchId.current;
      const [s, c, n, bo, pf, pe, t, fu, gf] = await Promise.allSettled([
        getSignals(), getCommentary(), getNews(), getBot(), getPortfolio(), getBotPerformance(), getTrades(), getFutures(), getGateFutures()]);
      if (s.status === "fulfilled") setSignals(s.value);
      if (c.status === "fulfilled") setCommentary(c.value);
      if (n.status === "fulfilled") setNews(n.value);
      if (bo.status === "fulfilled" && bid === botFetchId.current) setBot(bo.value);
      if (pf.status === "fulfilled") setPortfolio(pf.value);
      if (pe.status === "fulfilled") setPerf(pe.value);
      if (t.status === "fulfilled") setTrades(t.value);
      if (fu.status === "fulfilled") setFutures(fu.value);
      if (gf.status === "fulfilled") setGateFutures(gf.value);
    }, 20000);
    return () => clearInterval(iv);
  }, [bot?.active, autoRefresh]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    toast.info("AI piyasayı ve haberleri analiz ediyor...");
    try {
      const res = await runAnalyze();
      await refreshAll();
      toast.success(`${res.signals_created} yeni sinyal, ${res.news_analyzed} haber analiz edildi.`);
      setActive("signals");
    } catch (e) {
      toast.error("Analiz başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setAnalyzing(false); }
  };

  const handleApprove = async (signal) => {
    try {
      await approveSignal(signal.id);
      toast.success(`${signal.action === "BUY" ? "Alım" : "Satım"} işlemi gerçekleşti: ${signal.symbol}`);
      await Promise.all([getSignals().then(setSignals), getPortfolio().then(setPortfolio),
        getTrades().then(setTrades), getBotPerformance().then(setPerf)]);
    } catch (e) {
      toast.error("İşlem başarısız: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleReject = async (signal) => {
    try {
      await rejectSignal(signal.id);
      toast("Sinyal reddedildi.");
      await getSignals().then(setSignals);
    } catch (e) { toast.error("Hata: " + (e.response?.data?.detail || e.message)); }
  };

  const handleReset = async () => {
    await resetPortfolio();
    toast.success("Portföy sıfırlandı ($10,000).");
    await refreshAll();
  };

  const handleToggleAutoRefresh = async (val) => {
    setAutoRefreshState(val);
    try {
      await setAutoRefresh(val);
      toast.success(val ? "Otomatik analiz açıldı (her 5 dk)." : "Otomatik analiz kapatıldı.");
      if (val) setTimeout(refreshAll, 2000);
    } catch (e) {
      setAutoRefreshState(!val);
      toast.error("Ayar güncellenemedi");
    }
  };

  const handleRemoveCoin = async (symbol) => {
    try {
      const wl = await removeWatchlist(symbol);
      toast.success(`${symbol} listeden çıkarıldı.`);
      const p = await getPrices();
      setPrices(p);
      if (symbol === selected && p[0]) setSelected(p[0].symbol);
      void wl;
    } catch (e) { toast.error(e.response?.data?.detail || "Çıkarılamadı"); }
  };

  const handleCoinAdded = async () => {
    const p = await getPrices();
    setPrices(p);
  };

  const handleLogout = async () => {
    try { await authLogout(); } catch { /* ignore */ }
    logout();
  };

  const handleBotChanged = async (newBot) => {
    // Optimistically surface a just-started bot so its card appears immediately
    // (before the getBot round-trip), then reconcile with the server response.
    if (newBot && newBot.id) {
      setBot((prev) => {
        const active_bots = [newBot, ...((prev?.active_bots || []).filter((b) => b.id !== newBot.id))];
        const bots = [newBot, ...((prev?.bots || []).filter((b) => b.id !== newBot.id))];
        return { ...(prev || {}), active: true, active_count: active_bots.length, active_bots, bots };
      });
    }
    const bid = ++botFetchId.current;
    const [bo, pf, pe, fu, gf] = await Promise.allSettled([getBot(), getPortfolio(), getBotPerformance(), getFutures(), getGateFutures()]);
    if (bo.status === "fulfilled" && bid === botFetchId.current) setBot(bo.value);
    if (pf.status === "fulfilled") setPortfolio(pf.value);
    if (pe.status === "fulfilled") setPerf(pe.value);
    if (fu.status === "fulfilled") setFutures(fu.value);
    if (gf.status === "fulfilled") setGateFutures(gf.value);
  };

  const handleLaunchOpportunity = async (symbol, risk) => {
    try {
      const created = await startBot({ risk_mode: risk, duration_key: "1w", symbols: [symbol], market_type: "spot", leverage: 1 });
      toast.success(`Bot ${symbol} için başlatıldı (${risk === "low" ? "Düşük" : risk === "high" ? "Yüksek" : "Orta"} risk).`);
      await handleBotChanged(created);
      setActive("bot");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Başlatılamadı");
    }
  };

  const selectedPrice = prices.find((p) => p.symbol === selected) || prices[0];

  const showOverview = active === "overview";
  const showSignals = active === "overview" || active === "signals";
  const showPositions = active === "overview" || active === "positions";
  const showHistory = active === "overview" || active === "history";
  const showNewsSide = active === "overview";
  const gateReady = !!(binance?.connected && binance?.reachable);

  return (
    <div className="flex min-h-screen relative z-[1]">
      <Sidebar active={active} onNavigate={onNavigate} collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)} binanceConnected={gateReady} isAdmin={isAdmin} />

      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar
          onAnalyze={handleAnalyze} analyzing={analyzing}
          onConnect={() => onNavigate("api")} binanceConnected={gateReady}
          onReset={handleReset} autoRefresh={autoRefresh}
          onToggleAutoRefresh={handleToggleAutoRefresh} onLogout={handleLogout}
          active={active} onNavigate={onNavigate} isAdmin={isAdmin}
        />

        <main className="flex-1 p-4 md:p-6 space-y-6 pb-24 md:pb-6">
          {active === "simulations" ? (
            <SimulationsPage />
          ) : active === "reconciliation" ? (
            <ReconciliationPage />
          ) : active === "risk-profiles" ? (
            <RiskProfilesPage />
          ) : active === "admin" ? (
            <AdminPanel />
          ) : active === "api" ? (
            <ApiSettings onChanged={() => getGateStatus().then(setBinance)} />
          ) : active === "livetrades" ? (
            <LiveTrades />
          ) : active === "profile" ? (
            <Profile />
          ) : active === "notifications" ? (
            <Notifications />
          ) : (
          <>
          {showOverview && <LivePnlCard portfolio={portfolio} prices={prices} />}
          <PortfolioSummary portfolio={portfolio} />

          {showOverview && prices.length > 0 && (
            <PriceTicker prices={prices} selected={selected} onSelect={setSelected}
              onAdd={() => setAddCoinOpen(true)} onRemove={handleRemoveCoin} />
          )}

          {active === "futures" ? (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                <FuturesWalletCard data={gateFutures} />
                <BotManagementPanel bots={bot?.active_bots || []} events={bot?.events || []} onChanged={handleBotChanged} mode="futures" coins={prices} availableUsdt={portfolio?.available_usdt} futuresUsdt={gateFutures?.available} gateConnected={gateReady} />
                <FuturesPositions positions={futures} />
                <TradeHistory trades={trades} />
              </div>
              <div className="space-y-6">
                <BotPerformancePanel perf={perf} />
                <BotCommentary commentary={commentary} analyzing={analyzing} />
              </div>
            </div>
          ) : active === "bot" ? (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                <BotManagementPanel bots={bot?.active_bots || []} events={bot?.events || []} onChanged={handleBotChanged} mode="spot" coins={prices} availableUsdt={portfolio?.available_usdt} gateConnected={gateReady} />
                <StrategyPanel />
                <PositionsTable positions={portfolio?.positions || []} />
                <TradeHistory trades={trades} />
              </div>
              <div className="space-y-6">
                <BotPerformancePanel perf={perf} />
                <BotCommentary commentary={commentary} analyzing={analyzing} />
                <SignalFeed signals={signals} onApprove={handleApprove} onReject={handleReject} />
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              {showOverview && <OpportunityCorner opportunities={opportunities} onLaunch={handleLaunchOpportunity} />}
              {showOverview && <BotManagementPanel bots={bot?.active_bots || []} events={bot?.events || []} onChanged={handleBotChanged} mode="spot" coins={prices} availableUsdt={portfolio?.available_usdt} gateConnected={gateReady} />}
              {showOverview && <StrategyPanel />}
              {showOverview && selectedPrice && (
                <PriceChart symbol={selectedPrice.symbol} price={selectedPrice.price} change24h={selectedPrice.change24h} />
              )}
              {showPositions && <PositionsTable positions={portfolio?.positions || []} />}
              {showHistory && <TradeHistory trades={trades} />}
              {active === "news" && <NewsFeed news={news} />}
            </div>

            <div className="space-y-6">
              {showOverview && <BotPerformancePanel perf={perf} />}
              {active !== "news" && <BotCommentary commentary={commentary} analyzing={analyzing} />}
              {showSignals && <SignalFeed signals={signals} onApprove={handleApprove} onReject={handleReject} />}
              {showNewsSide && <NewsFeed news={news.slice(0, 10)} />}
            </div>
          </div>
          )}
          </>
          )}

          <footer className="pt-4 border-t border-[var(--border)] text-[11px] text-[var(--text-faint)] font-mono flex flex-wrap gap-x-4 gap-y-1">
            <span>Piyasa verisi: CoinGecko (canlı)</span><span>·</span>
            <span>AI: Claude Sonnet 4.6</span><span>·</span>
            <span>Bot varsayılan Simülasyon; "Gerçek" modda ve manuel Gate.io işlemleri GERÇEK PARADIR · Yatırım tavsiyesi değildir</span>
            <span className="w-full text-[var(--text-faint)] mt-1">{COPYRIGHT}</span>
          </footer>
        </main>
      </div>

      <BottomNav active={active} onNavigate={onNavigate} />

      <ConnectBinanceDialog open={binanceOpen} onOpenChange={setBinanceOpen} status={binance}
        onChanged={() => getGateStatus().then(setBinance)} />
      <AddCoinDialog open={addCoinOpen} onOpenChange={setAddCoinOpen}
        onAdded={handleCoinAdded} existing={prices} />
    </div>
  );
}
